/**
 * Pre-trading workflow constants
 *
 * Runtime constants for pre-trading configuration.
 * Types derived from these constants live in src/types/schema/PreTradingSettings.ts.
 */

import type { TransitionQuote } from '@/types/schema/PreTradingSettings';

/**
 * Valid section identifiers for pre-trading workflow.
 * Use this array when iterating over sections in UI logic.
 */
export const PRE_TRADING_SECTION_IDS = [
  'personalPrep',
  'healthMetrics',
  'objectives',
  'economicData',
  'preMarketAnalysis',
  'tradingSetup',
  'finalChecks',
] as const;

/**
 * Lucide icon names used for pre-trading sections.
 * Component layer maps these to actual LucideIcon components.
 */
export type PreTradingIconName =
  | 'Heart'
  | 'Activity'
  | 'Target'
  | 'Calendar'
  | 'LineChart'
  | 'Crosshair'
  | 'CheckSquare';

/**
 * Metadata for a pre-trading section used in UI rendering.
 */
export interface PreTradingSectionMeta {
  /** Lucide icon name for component lookup */
  icon: PreTradingIconName;
  /** Display title */
  title: string;
  /** Short description (≤50 chars for mobile) */
  description: string;
  /** Route path for settings page, null if no page yet */
  settingsRoute: string | null;
  /** Feature not yet built */
  isComingSoon: boolean;
  /** Can be drag-reordered (false for fixed sections like tradingSetup, finalChecks) */
  isOrderable: boolean;
  /** Can be hidden by the user (false = always visible, forced visible on read & write) */
  isOptional: boolean;
}

/**
 * Section metadata keyed by PreTradingSectionId.
 * Used by PreTradingDashboard for schema-driven rendering.
 */
export const PRE_TRADING_SECTION_META: Record<
  (typeof PRE_TRADING_SECTION_IDS)[number],
  PreTradingSectionMeta
> = {
  personalPrep: {
    icon: 'Heart',
    title: 'Personal Preparation',
    description: 'Health, todos, and readiness',
    settingsRoute: '/settings/workflow/pre-trading/personal-prep',
    isComingSoon: false,
    isOrderable: true,
    isOptional: true,
  },
  healthMetrics: {
    icon: 'Activity',
    title: 'Health Metrics',
    description: 'Sleep, strain, and recovery',
    settingsRoute: '/settings/workflow/pre-trading/personal-prep',
    isComingSoon: false,
    isOrderable: true,
    isOptional: true,
  },
  objectives: {
    icon: 'Target',
    title: 'Trading Objectives',
    description: 'Define daily trading goals',
    settingsRoute: null,
    isComingSoon: false,
    isOrderable: true,
    isOptional: true,
  },
  economicData: {
    icon: 'Calendar',
    title: 'Economic Data',
    description: 'Review economic calendar events',
    settingsRoute: '/settings/workflow/economic-calendar',
    isComingSoon: false,
    isOrderable: true,
    isOptional: true,
  },
  preMarketAnalysis: {
    icon: 'LineChart',
    title: 'Pre-Market Snapshot',
    description: 'Capture market conditions',
    settingsRoute: null,
    isComingSoon: false,
    isOrderable: true,
    isOptional: true,
  },
  tradingSetup: {
    icon: 'Crosshair',
    title: 'Trading Setup',
    description: 'Planned strategies, account, and instruments',
    settingsRoute: null,
    isComingSoon: false,
    isOrderable: false,
    isOptional: false,
  },
  finalChecks: {
    icon: 'CheckSquare',
    title: 'Final Checks',
    description: 'Last verification before trading',
    settingsRoute: null,
    isComingSoon: false,
    isOrderable: false,
    isOptional: true,
  },
};

/** Section item shape shared by both derive functions */
interface SectionItem {
  id: (typeof PRE_TRADING_SECTION_IDS)[number];
  visible: boolean;
  order: number;
}

/**
 * Normalize fixed sections after merging config with defaults.
 *
 * Driven by `isOptional` and `isOrderable` metadata — no per-ID special cases.
 * 1. Injects any missing non-optional sections (legacy drafts that predate them)
 * 2. Forces `visible: true` for non-optional sections
 * 3. Pins non-orderable sections at the end in declaration order
 */
export function normalizeFixedSections(merged: SectionItem[]): SectionItem[] {
  const mergedMap = new Map(merged.map((s) => [s.id, s]));

  // Inject missing non-optional sections
  for (const id of PRE_TRADING_SECTION_IDS) {
    if (!PRE_TRADING_SECTION_META[id].isOptional && !mergedMap.has(id)) {
      mergedMap.set(id, { id, visible: true, order: mergedMap.size });
    }
  }

  // Force visible for non-optional sections
  for (const [id, item] of mergedMap) {
    if (!PRE_TRADING_SECTION_META[id].isOptional) {
      item.visible = true;
    }
  }

  // Split orderable vs non-orderable
  const orderable: SectionItem[] = [];
  const fixed: SectionItem[] = [];
  for (const item of mergedMap.values()) {
    if (PRE_TRADING_SECTION_META[item.id].isOrderable) {
      orderable.push(item);
    } else {
      fixed.push(item);
    }
  }

  // Sort orderable by their current order
  orderable.sort((a, b) => a.order - b.order);

  // Sort fixed sections in declaration order (PRE_TRADING_SECTION_IDS)
  const declarationIndex = new Map(PRE_TRADING_SECTION_IDS.map((id, i) => [id, i]));
  fixed.sort((a, b) => (declarationIndex.get(a.id) ?? 0) - (declarationIndex.get(b.id) ?? 0));

  // Pin fixed sections after orderable, with normalized order values
  return [
    ...orderable.map((s, i) => ({ ...s, order: i })),
    ...fixed.map((s, i) => ({ ...s, order: orderable.length + i })),
  ];
}

/**
 * Default motivational quotes for the transition screen.
 *
 * These quotes are used when creating new PreTradingSettings and
 * can be restored via the "Reset to Defaults" action in the modal.
 *
 * Note: When using this constant for defaults, clone the array to avoid
 * shared references (e.g., `DEFAULT_TRANSITION_QUOTES.map(q => ({ ...q }))`).
 */
export const DEFAULT_TRANSITION_QUOTES: readonly TransitionQuote[] = [
  {
    id: 'default-quote-1',
    text: 'What you track, you can transform.',
  },
  {
    id: 'default-quote-2',
    text: 'Silence the noise. Trust the signal.',
  },
  {
    id: 'default-quote-3',
    text: 'Trade your plan. Review your trades. Refine your plan.',
  },
  {
    id: 'default-quote-4',
    text: 'Process is the edge. Results are the echo.',
  },
  {
    id: 'default-quote-5',
    text: 'Today\'s discipline is tomorrow\'s data.',
  },
] as const;
